<template>
  <div>
    <p
      v-if="intro"
      class="mt-3 intro intro-style"
    >
      Vous pouvez saisir le numéro adhérent ou le nom de la personne ou de
      l'exploitation recherchée.
    </p>
    <v-autocomplete
      v-model="tiersSelected"
      :search-input.sync="searchInput"
      :class="intro ? 'mt-10' : ''"
      :loading="loadingTiers"
      :items="clonedResults"
      label="Rechercher un adhérent"
      item-title="query"
      item-value="numtiers"
      menu-icon=""
      append-icon=""
      append-outer-icon="mdi-magnify"
      :filter="() => true"
      return-object
      attach
      placeholder="Recherchez par numéro adhérent ou par nom (ex: 800 ou Mont)"
      :menu-props="{
        closeOnContentClick: true
      }"
      autofocus
    >
      <template v-slot:selection="{ item }">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="intro titre-style secondary--text">
              {{ item.raisonSociale }}
            </v-list-item-title>
            <v-list-item-title class="intro intro-style">
              {{ buildInfosTiers(item) }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-slot:item="{ item }">
        <v-list-item class="pointer-style">
          <v-list-item-content @click="onTiersSelected(item)">
            <v-list-item-title class="intro titre-style secondary--text">
              {{ item.raisonSociale }}
            </v-list-item-title>
            <v-list-item-title class="intro intro-style">
              {{ buildInfosTiers(item) }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template
        v-if="(hasMoreTiers && clonedResults.length > 0) || loadingMoreTiers"
        v-slot:append-item
      >
        <AgrButton
          outlined
          block
          :loading="loadingMoreTiers"
          elevation="0"
          @click.stop="loadMoreResults"
        >
          Plus de résultats
        </AgrButton>
      </template>
      <template v-slot:no-data>
        <BaseLoader v-if="loadingTiers" />
        <span
          v-else-if="searchInput && searchInput.length && searchInput.length < 3"
          class="mx-3"
        >Continuez votre saisie...</span>
        <span v-else-if="searchInput && !searchInput.length" />
        <span
          v-else
          class="mx-3"
        >Aucun adhérent trouvé.</span>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import {clone} from '@/utils/clone'
import BaseLoader from '@/components/common/base-loader.vue'
import { AgrButton } from '@agrial/ui'

export default {
  name: 'RechercheAdherent',
  components: {
    BaseLoader,
    AgrButton
  },
  props: {
    intro: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      serverError: false,
      tiersSelected: {
        query: '',
        raisonSociale: '',
        numtiers: '',
        responsable: {
          nom: ''
        }
      },
      loadingTiers: false,
      loadingMoreTiers: false,
      clonedResults: [],
      searchInput: null,
      debounceTime: 500,
      pageNumber: 0,
      pageSize: 20,
      tiersSearchResults: []
    }
  },
  computed: {
    hasMoreTiers () {
      this.pageNumber === this.pageNumber + 1
      return (
        this.tiersSearchResults.length >=
        this.pageSize + this.pageSize * this.pageNumber
      )
    }
  },
  watch: {
    searchInput (newSearchInput, oldSearchInput) {
      this.loadingTiers = true
      // vérifie qu'il y a bien une query saisie et lance la recherche
      if (!!newSearchInput && newSearchInput !== oldSearchInput) {
        this.rechercherTiersDebounced(newSearchInput)
      } else {
        this.loadingTiers = false
      }
    }
  },
  methods: {
    ...mapActions({
      resetAdherent: 'adherent/resetNumtiers',
      selectAdherent: 'adherent/selectAdherent',
      search: 'tiers/search',
      searchMore: 'tiers/searchMore'
    }),
    onTiersSelected (tiers) {
      if (tiers?.numtiers) {
        this.resetAdherent()
        this.selectAdherent(tiers)
        this.$emit('onCloseRecherche', tiers)
      }
    },
    rechercherTiersDebounced (query) {
      let timerId
      // annule l'appel
      clearTimeout(timerId)

      // delai la recherche
      timerId = setTimeout(() => {
        this.rechercherTiers(query)
      }, this.debounceTime)
    },
    rechercherTiers (query) {
      this.serverError = null
      this.loadingTiers = true
      if (query.length >= 3) {
        this.loadingTiers = true
        this.search({ query })
          .then(results => {
            this.clonedResults = clone(results)
            this.loadingTiers = false
          })
          .catch((error) => {
            if (error.code !== 'ERR_CANCELED') {
              this.serverError = !error.isCancel
            }
          })
      } else {
        this.clonedResults.splice(0)
        this.loadingTiers = false
      }
    },
    buildInfosTiers (tiers) {
      let infoTiers = tiers.numtiers
        ? tiers.numtiers +
          '-' +
          (tiers.responsable ? tiers.responsable.nom : '') +
          '-' +
          (tiers.ville ? tiers.ville : '')
        : ''

      return infoTiers
    },
    loadMoreResults () {
      this.loadingMoreTiers = true
      /** Chaine recherchée */
      this.searchMore({ query: this.searchInput }, this.pageNumber)
        .then(results => this.clonedResults = clone(results))
        .catch(() => {
          this.serverError = true
        })
        .finally(() => {
          this.loadingMoreTiers = false
        })
    },
  }
}
</script>

<style lang="scss" scoped>
.intro-style {
  font-family: "OpenSans", "sans-serif" !important;
  font-size: 14px !important;
  line-height: normal !important;
  align-self: stretch !important;
  font-style: normal !important;
}

.titre-style {
  font-family: "OpenSans", "sans-serif" !important;
  color: var(--vert);
  font-style: normal;
  align-self: stretch;
  line-height: normal;
}

.pointer-style {
  cursor: pointer;
}

.v-list-item:hover {
  background-color: #e0e0e0 !important;
}
</style>